import {
  ActionBarSettings,
  EmptyStateSettings,
  ImportIcon,
  PrinterSettings,
  TableColumns,
  useDefaultSelecting,
  VisibilityState,
} from '@bp/ui-components';
import { OnChangeFn, RowSelectionState, SortingState, Updater } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { useColumnsSort } from '../../../hooks/useColumnsSort';
import { useConfirm } from '../../../hooks/useConfirm';
import { useHiddenColumns } from '../../../hooks/useHiddenColumns';
import { useLoadBasicData } from '../../../hooks/useLoadBasicData';
import { useUserConfigContext } from '../../../hooks/useUserConfigContext';
import { FunctionalClassSubstitutionBehavior } from '../../../types/planung-graphql-client-defs';
import { hexToColorOption } from '../../../utils/colorUtils';
import { sortClasses } from '../../../utils/sortHelper';
import { ClassesTableColumns, ClassesTableType } from '../types/ClassesTypes';

type useClassesTableProps = {
  context?:
    | 'deputate'
    | 'timetable'
    | 'settings'
    | 'divisions'
    | 'function'
    | 'timetable-version'
    | 'timetable-version-import';
  columns?: ClassesTableColumns;
};

type TableSettings = {
  showBorderRadius: boolean;
  showShadow: boolean;
  canScroll: boolean;
  isOnWhite: boolean;
  showActionBar: boolean;
  minHeight: number;
  breakpoint: string | null;
  lastColWidth: string;
};

type TableVisbility = {
  showVisibility: boolean;
  columnVisibility: VisibilityState;
  onColumnVisibilityChange: (updaterOrValue: Updater<VisibilityState>) => void;
};

type TableSorting = {
  showSort: boolean;
  sorting: SortingState;
  onSortingChange: (updaterOrValue: Updater<SortingState>) => void;
};

type TableSelection = {
  showSelect: boolean;
  rowSelection: RowSelectionState;
  onRowSelectionChange: OnChangeFn<RowSelectionState>;
  clearSelection: () => void;
};

type UseClassesTableReturn = {
  data: ClassesTableType[];
  columns: TableColumns<ClassesTableType>[];
  tableSettings: TableSettings;
  tableVisibility: TableVisbility;
  tableSorting: TableSorting;
  tableSelection: TableSelection;
  emptyStateSettings: EmptyStateSettings;
  printerSettings: PrinterSettings;
  actionBarSettings: ActionBarSettings;
  DeleteDialog: React.FC<any>;
  confirmDelete: () => Promise<boolean>;
};

export const useClassesTable = (props?: useClassesTableProps): UseClassesTableReturn => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const { selectedSchoolYear } = useUserConfigContext();

  const { context, columns } = props ?? {};

  const tableSettings: TableSettings = {
    showBorderRadius: true,
    showShadow: true,
    canScroll: true,
    isOnWhite: false,
    showActionBar: true,
    minHeight: 600,
    breakpoint: null,
    lastColWidth: '68px',
  };

  const { columnVisibility, saveColumnVisibility: onColumnVisibilityChange } = useHiddenColumns(
    `${context}-classes-table`,
    {
      tutor3: false,
      printOrder: false,
      printPageNr: false,
      room: false,
      color: false,
      relevantForSubstitution: false,
    },
  );

  const tableVisibility: TableVisbility = {
    showVisibility: true,
    columnVisibility,
    onColumnVisibilityChange,
  };

  const { sorting, saveSorting: onSortingChange } = useColumnsSort(`${context}-classes-table`);

  const tableSorting: TableSorting = {
    showSort: true,
    sorting,
    onSortingChange,
  };

  const { rowSelection, onRowSelectionChange, clearSelection } = useDefaultSelecting();

  const tableSelection: TableSelection = {
    showSelect: true,
    rowSelection,
    onRowSelectionChange,
    clearSelection,
  };

  const { ConfirmationDialog: DeleteDialog, confirm: confirmDelete } = useConfirm({
    defaultTitle: t('delete.headline'),
    defaultMessage: t('delete.message', {
      type: t('classes.title', { count: 1 }),
      context: 'female',
      count: 1,
    }),
    defaultConfirmText: t('delete.delete'),
  });

  const emptyStateSettings: EmptyStateSettings = {
    showButton: true,
    buttonIcon: <ImportIcon />,
    buttonText: t('common.inheritFromPreviousYear'),
  };

  const printerSettings: PrinterSettings = {
    headline: pimAuthClaims.getProfile()?.organization.name,
    subline: `${t('classes.title', { count: 2 })} - ${t('common.schoolYear')} ${selectedSchoolYear?.shortName}`,
    filename: `${t('classes.title', { count: 2 })}_${selectedSchoolYear?.shortName}`,
  };

  const actionBarSettings: ActionBarSettings = {
    showExpertFilter: true,
    showAddButton: selectedSchoolYear?.name !== '',
    showPrintButton: true,
  };

  const { roomsData, gradeGroupData, teacherData, classesData } = useLoadBasicData({ pause: false });

  const classes = classesData.classes.sort(sortClasses);
  const gradeGroups = gradeGroupData?.gradeGroups ?? [];
  const teachers = teacherData.people;
  const rooms = roomsData.rooms;

  /**
   * Maps incoming data to ClassesTableType
   *
   * @returns {ClassesTableType[]} An array of mapped data to use in classes tables
   */

  const data: ClassesTableType[] = useMemo(() => {
    return classes.map((c) => {
      const { html, label } = hexToColorOption(c.timetableConfig?.color ?? '');
      const gradeGroup = gradeGroups.find((gg) => gg.uuid === c.gradeGroup?.uuid);
      const tutor1 = teachers.find((t) => t.uuid === c.tutorsConnection.edges[0]?.node.uuid);
      const tutor2 = teachers.find((t) => t.uuid === c.tutorsConnection.edges[1]?.node.uuid);
      const tutor3 = teachers.find((t) => t.uuid === c.tutorsConnection.edges[2]?.node.uuid);
      const room = rooms.find((room) => room.uuid === c.defaultRoom?.uuid)?.name ?? '';

      return {
        uuid: c.uuid,
        grade: c.grade || c.grade === 0 ? c.grade.toString() : 'F',
        name: c.name,
        printOrder: c.timetableConfig?.printOrder ?? 0,
        printPageNr: c.timetableConfig?.printPageNr ?? 0,
        shortName: c.shortName,
        tutor1: tutor1?.displayNameShort ?? '',
        tutor2: tutor2?.displayNameShort ?? '',
        tutor3: tutor3?.displayNameShort ?? '',
        gradeGroup: gradeGroup?.name ?? '',
        room,
        divisionsCount: c.divisions.length ?? 0,
        color: {
          color: html,
          colorLabel: label,
        },
        functionClass: c.grade === undefined || c.grade === null,
        functionalClassSubstitutionBehavior: c.functionalClassSubstitutionBehavior || null,
        relevantForSubstitution: c.relevantForSubstitution ?? false,
      };
    });
  }, [classes, gradeGroups, teachers, rooms]);

  const tColumns = useMemo(() => {
    const tableColumns: TableColumns<ClassesTableType>[] = [];

    if (columns?.includes('name')) {
      tableColumns.push({
        header: t('common.name'),
        accessorKey: 'name',
        id: 'name',
        size: 200,
        canExpand: context === 'divisions',
      });
    }

    if (columns?.includes('shortName')) {
      tableColumns.push({
        header: t('classes.shortName'),
        accessorKey: 'shortName',
        id: 'shortName',
        size: 75,
      });
    }

    if (columns?.includes('grade')) {
      tableColumns.push({
        header: t('classes.level.short'),
        meta: {
          filterName: t('classes.level.full'),
          tooltip: t('classes.level.full'),
        },
        accessorKey: 'grade',
        id: 'grade',
        size: 75,
        cell: ({ row }) => {
          return row.original.grade ?? 'F';
        },
        accessorFn: ({ grade }) => {
          return grade ?? 'F';
        },
      });
    }

    if (columns?.includes('gradeGroup')) {
      tableColumns.push({
        header: t('gradeGroups.title', { context: 'short' }),
        meta: {
          filterName: t('gradeGroups.title', { count: 1 }),
          tooltip: t('gradeGroups.title', { count: 1 }),
        },
        id: 'gradeGroup',
        accessorKey: 'gradeGroup',
        size: 75,
      });
    }

    if (columns?.includes('tutor1')) {
      tableColumns.push({
        header: `${t('classes.tutor.short')} 1`,
        meta: {
          filterName: `${t('classes.tutor.full')} 1`,
          tooltip: `${t('classes.tutor.full')} 1`,
        },
        size: 200,
        id: 'tutor1',
        accessorKey: 'tutor1',
      });
    }

    if (columns?.includes('tutor2')) {
      tableColumns.push({
        header: `${t('classes.tutor.short')} 2`,
        meta: {
          filterName: `${t('classes.tutor.full')} 2`,
          tooltip: `${t('classes.tutor.full')} 2`,
        },
        size: 200,
        id: 'tutor2',
        accessorKey: 'tutor2',
      });
    }

    if (columns?.includes('tutor3')) {
      tableColumns.push({
        header: `${t('classes.tutor.short')} 3`,
        meta: {
          filterName: `${t('classes.tutor.full')} 3`,
          tooltip: `${t('classes.tutor.full')} 3`,
        },
        size: 200,
        id: 'tutor3',
        accessorKey: 'tutor3',
      });
    }

    if (columns?.includes('room')) {
      tableColumns.push({
        header: t('rooms.title', { count: 1 }),
        meta: {
          filterName: t('rooms.standard'),
          tooltip: t('rooms.standard'),
        },
        id: 'room',
        accessorKey: 'room',
        size: 200,
      });
    }

    if (columns?.includes('printOrder')) {
      tableColumns.push({
        header: t('classes.printOrder.short'),
        meta: {
          filterName: t('classes.printOrder.full'),
          tooltip: t('classes.printOrder.full'),
        },
        accessorKey: 'printOrder',
        id: 'printOrder',
        size: 40,
      });
    }

    if (columns?.includes('printPageNr')) {
      tableColumns.push({
        header: t('classes.printPageNr.short'),
        meta: {
          filterName: t('classes.printPageNr.full'),
          tooltip: t('classes.printPageNr.full'),
        },
        accessorKey: 'printPageNr',
        id: 'printPageNr',
        size: 40,
      });
    }

    if (columns?.includes('divisionsCount')) {
      tableColumns.push({
        header: t('divisions.title', { count: 2 }),
        accessorKey: 'divisionsCount',
        id: 'divisionsCount',
        enableGlobalFilter: false,
        enableColumnFilter: false,
        size: 150,
      });
    }

    if (columns?.includes('color')) {
      tableColumns.push({
        header: t('common.color'),
        accessorKey: 'color',
        id: 'color',
        type: 'color',
      });
    }

    if (columns?.includes('functionalClassSubstitutionBehavior')) {
      tableColumns.push({
        header: t('functionClass.functionalClassSubstitutionBehavior', { count: 1 }),
        accessorKey: 'functionalClassSubstitutionBehavior',
        id: 'functionalClassSubstitutionBehavior',
        size: 300,
        cell: ({ row }) => {
          switch (row.original.functionalClassSubstitutionBehavior) {
            case FunctionalClassSubstitutionBehavior.AttendancePlanSource:
              return t('functionClass.attendancePlanSource', { count: 1 });
            case FunctionalClassSubstitutionBehavior.ShowInSubstitutionPlan:
              return t('functionClass.showInSubstitutionPlan', { count: 1 });
            case FunctionalClassSubstitutionBehavior.NormalClassBehavior:
              return t('functionClass.normalClassBehavior', { count: 1 });
            default:
              return '';
          }
        },
      });
    }

    if (columns?.includes('relevantForSubstitution')) {
      tableColumns.push({
        header: t('classes.relevantForSubstitution'),
        accessorKey: 'relevantForSubstitution',
        id: 'relevantForSubstitution',
        size: 150,
        type: 'boolean',
      });
    }

    return tableColumns;
  }, [t, columns, context]);

  return {
    data: data,
    columns: tColumns,
    tableSettings,
    tableSorting,
    tableVisibility,
    tableSelection,
    emptyStateSettings,
    printerSettings,
    actionBarSettings,
    DeleteDialog,
    confirmDelete,
  };
};
